// Print template builder shell.
//
// Phase 5 wired the columns-mode editors. The Hybrid Builder Extension
// adds a mode toggle at the top of the editor panel: "Visual Builder"
// (mode='columns') or "Custom HTML" (mode='custom_html'). The active
// mode swaps the central editor between the four section editors and
// the free-form CustomHtmlEditor; the LivePreview iframe is mode-
// agnostic — the server renderer handles both paths.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../../hooks/useToast';
import { useCompanyOverview } from '../company/hooks/useCompany';
import {
  usePrintTemplate,
  useUpsertPrintTemplate,
} from './hooks/usePrintTemplates';
import { HeaderEditor } from './components/HeaderEditor';
import { BodyEditor } from './components/BodyEditor';
import { FooterEditor } from './components/FooterEditor';
import { PageEditor } from './components/PageEditor';
import { LivePreview } from './components/LivePreview';
import { LogoRequiredModal } from './components/LogoRequiredModal';
import { CustomHtmlEditor } from './components/CustomHtmlEditor';
import { validateForType } from './validation';
import type {
  PrintTemplateContent,
  PrintTemplateMode,
  PrintTemplateType,
  HeaderConfig,
  BodyConfig,
  FooterConfig,
  PageConfig,
} from '../../../types/api';
import styles from './PrintTemplateBuilderPage.module.css';

const TYPE_LABELS: Record<PrintTemplateType, string> = {
  diary_results: 'Diary Results',
  receipt: 'Receipt',
  invoice: 'Invoice',
  wb_ticket: 'Weighbridge Ticket',
  wb_advice_note: 'Advice Note',
  wb_wtn: 'Waste Transfer Note',
  wb_hazardous_consignment: 'Hazardous Consignment Note',
};

const VALID_TYPES: ReadonlySet<string> = new Set([
  'diary_results',
  'receipt',
  'invoice',
  'wb_ticket',
  'wb_advice_note',
  'wb_wtn',
  'wb_hazardous_consignment',
]);

function isPrintTemplateType(value: string): value is PrintTemplateType {
  return VALID_TYPES.has(value);
}

/** Stable structural-equality check for the unsaved-changes indicator. */
function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function modeOf(content: PrintTemplateContent | null): PrintTemplateMode {
  return content?.mode ?? 'columns';
}

export function PrintTemplateBuilderPage(): JSX.Element {
  const { templateType: rawType } = useParams<{ templateType: string }>();
  const toast = useToast();

  // -------- type guard --------------------------------------------------
  if (typeof rawType !== 'string' || !isPrintTemplateType(rawType)) {
    return (
      <div className={styles.shell}>
        <PageHeader title="Unknown template" />
        <Card>
          <p>That template type does not exist.</p>
          <Link to="/settings/print-templates" className={styles.backLink}>
            <Button variant="secondary" icon={ArrowLeft}>
              Back to list
            </Button>
          </Link>
        </Card>
      </div>
    );
  }
  const type: PrintTemplateType = rawType;

  // -------- queries -----------------------------------------------------
  const detail = usePrintTemplate(type);
  const company = useCompanyOverview();
  const upsert = useUpsertPrintTemplate();

  // -------- local content state -----------------------------------------
  const serverContent = detail.data?.template_content;
  const [content, setContent] = useState<PrintTemplateContent | null>(null);
  const [pendingMode, setPendingMode] = useState<PrintTemplateMode | null>(null);

  useEffect(() => {
    if (serverContent !== undefined && content === null) {
      setContent(serverContent);
    }
  }, [serverContent, content]);

  const dirty = useMemo(() => {
    if (content === null || serverContent === undefined) return false;
    return !deepEqual(content, serverContent);
  }, [content, serverContent]);

  // beforeunload guard while dirty.
  useEffect(() => {
    if (!dirty) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  // -------- mode switching ----------------------------------------------
  const activeMode = modeOf(content);

  function requestModeSwitch(next: PrintTemplateMode) {
    if (next === activeMode) return;
    setPendingMode(next);
  }

  function commitModeSwitch() {
    if (content === null || pendingMode === null) return;
    if (pendingMode === 'custom_html') {
      // Switching to custom_html: keep header/body/footer/page intact
      // (unused but harmless), seed empty html/css, drop the columns
      // because the renderer ignores them and the validator rejects
      // a non-empty list when mode='custom_html'.
      setContent({
        ...content,
        mode: 'custom_html',
        body: { ...content.body, columns: [] },
        custom_html: content.custom_html ?? '',
        custom_css: content.custom_css ?? '',
      });
    } else {
      // Switching back to columns: drop the html/css fields so the
      // saved JSON is small and the schema doesn't carry a stale
      // payload. Body/header/footer/page survive — they were never
      // mutated while the user was editing custom_html.
      setContent({
        ...content,
        mode: 'columns',
        custom_html: undefined,
        custom_css: undefined,
      });
    }
    setPendingMode(null);
  }

  function cancelModeSwitch() {
    setPendingMode(null);
  }

  // -------- save --------------------------------------------------------
  function handleSave() {
    if (content === null) return;
    const validation = validateForType(type, content);
    if (!validation.ok) {
      const firstError = Object.values(validation.errors)[0] ?? 'Invalid template';
      toast.error(firstError);
      return;
    }
    upsert.mutate(
      { type, body: { template_content: content } },
      {
        onSuccess: () => {
          toast.success('Template saved.');
        },
        onError: () => {
          toast.error('Failed to save template.');
        },
      },
    );
  }

  // -------- render branches --------------------------------------------
  const companyHasLogo =
    typeof company.data?.company.logo_url === 'string' &&
    company.data.company.logo_url.length > 0;

  // Logo guard only matters in columns mode — custom_html sets its own
  // page chrome, so the header.logo flag doesn't affect what gets
  // rendered.
  const showLogoRequiredModal =
    activeMode === 'columns' &&
    content !== null &&
    company.isSuccess &&
    content.header.logo.show &&
    !companyHasLogo;

  function handleDisableLogo() {
    if (content === null) return;
    setContent({
      ...content,
      header: { ...content.header, logo: { ...content.header.logo, show: false } },
    });
  }

  return (
    <div className={styles.shell}>
      <PageHeader
        title={`Edit ${TYPE_LABELS[type]} template`}
        subtitle={
          detail.data !== undefined ? (
            <span className={styles.subRow}>
              <span>{detail.data.template_name}</span>
              {detail.data.is_custom ? (
                <Badge variant="info">Custom</Badge>
              ) : (
                <Badge variant="muted">Default (inherited)</Badge>
              )}
              {dirty ? <Badge variant="warning">Unsaved changes</Badge> : null}
            </span>
          ) : null
        }
        actions={
          <Link to="/settings/print-templates" className={styles.backLink}>
            <Button variant="secondary" icon={ArrowLeft}>
              Back
            </Button>
          </Link>
        }
      />

      {detail.isLoading || content === null ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading template" />
          </div>
        </Card>
      ) : detail.isError ? (
        <Card title="Could not load template">
          <p className={styles.errorText}>
            We hit a problem fetching the template. Try again in a moment.
          </p>
          <Button variant="primary" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </Card>
      ) : (
        <div className={styles.layout}>
          <div className={styles.editors}>
            <div
              className={styles.modeBar}
              role="group"
              aria-label="Editor mode"
            >
              <button
                type="button"
                className={styles.modeBtn}
                aria-pressed={activeMode === 'columns'}
                onClick={() => requestModeSwitch('columns')}
              >
                Visual Builder
              </button>
              <button
                type="button"
                className={styles.modeBtn}
                aria-pressed={activeMode === 'custom_html'}
                onClick={() => requestModeSwitch('custom_html')}
              >
                Custom HTML
              </button>
            </div>

            {activeMode === 'columns' ? (
              <>
                <HeaderEditor
                  value={content.header}
                  onChange={(next: HeaderConfig) =>
                    setContent({ ...content, header: next })
                  }
                  companyHasLogo={companyHasLogo}
                  templateType={type}
                />

                <BodyEditor
                  value={content.body}
                  onChange={(next: BodyConfig) =>
                    setContent({ ...content, body: next })
                  }
                  templateType={type}
                />

                <FooterEditor
                  value={content.footer}
                  onChange={(next: FooterConfig) =>
                    setContent({ ...content, footer: next })
                  }
                  templateType={type}
                />

                <PageEditor
                  value={content.page}
                  onChange={(next: PageConfig) =>
                    setContent({ ...content, page: next })
                  }
                />
              </>
            ) : (
              <CustomHtmlEditor
                htmlValue={content.custom_html ?? ''}
                cssValue={content.custom_css ?? ''}
                onHtmlChange={(next: string) =>
                  setContent({ ...content, custom_html: next })
                }
                onCssChange={(next: string) =>
                  setContent({ ...content, custom_css: next })
                }
                templateType={type}
              />
            )}

            <div className={styles.saveBar}>
              <span className={styles.saveHint}>
                {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
              </span>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                loading={upsert.isPending}
                disabled={!dirty || upsert.isPending}
              >
                Save template
              </Button>
            </div>
          </div>

          <aside className={styles.preview}>
            <Card title="Preview" flush>
              <LivePreview templateType={type} templateContent={content} />
            </Card>
          </aside>
        </div>
      )}

      <LogoRequiredModal
        open={showLogoRequiredModal}
        onDisableLogo={handleDisableLogo}
        onClose={handleDisableLogo}
      />

      <ConfirmModal
        open={pendingMode === 'custom_html'}
        title="Switch to Custom HTML?"
        message={
          <>
            Switching to Custom HTML mode will replace the visual
            builder. Your current visual settings will not be
            converted. You can switch back to Visual Builder at any
            time, but your custom HTML will be lost.
          </>
        }
        confirmLabel="Switch to Custom HTML"
        cancelLabel="Cancel"
        onConfirm={commitModeSwitch}
        onCancel={cancelModeSwitch}
      />
      <ConfirmModal
        open={pendingMode === 'columns'}
        title="Switch back to Visual Builder?"
        message={
          <>
            Switching back to Visual Builder will discard your custom
            HTML and CSS. The visual section settings will be reused.
          </>
        }
        confirmLabel="Switch to Visual Builder"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={commitModeSwitch}
        onCancel={cancelModeSwitch}
      />
    </div>
  );
}
